/**
 * Every date the interface shows, in one place.
 *
 * Thirty call sites formatted dates by hand and disagreed in two ways that a reader notices.
 *
 * Six of them passed 'ro-RO' or 'ro-MD' as a literal, so an English visitor read "01.09.2026" and
 * "1 septembrie 2026" on a page that was otherwise in English. One — the public training calendar
 * — passed no locale at all, which hands the decision to the browser: the same card said
 * "9/1/2026" to a visitor whose machine is set to US English and "01.09.2026" to the next one.
 *
 * And the numeric shape did not match the design system. MUD writes a date as DD/MM/YYYY (see
 * Components/input-date.html, "Enter date in DD/MM/YYYY format", and every placeholder in
 * input-date-picker.html); `toLocaleDateString('ro-RO')` writes 01.09.2026, with dots. The
 * DatePicker already produced slashes, so a date typed into a form and the same date read back in
 * a table were punctuated differently.
 *
 * The format is therefore built by hand rather than delegated to Intl: MUD's is the same in both
 * languages, and asking Intl for it would mean trusting each locale's idea of a separator.
 *
 * There is deliberately no spelled-out variant. "1 septembrie 2026" read better in prose and was
 * what most screens showed, but two formats meant a reader met the same date written two ways
 * depending on which page they were on, and the design system names only one. `language` stays in
 * the signatures — unused, since the format does not vary by language — so a call site does not
 * have to be edited if that ever stops being true.
 */

type Language = string;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** `HH:mm`, 24-hour, in both languages — the shape TimePicker writes and the forms accept. */
function time(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** MUD's numeric date: `01/09/2026`, identical in Romanian and English. */
export function formatDate(iso: string | null | undefined, _language?: Language): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** The time alone, `10:00`, for a table that shows the date and the time as two parts. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : time(d);
}

/** MUD's numeric date with the time: `01/09/2026, 10:00`. */
export function formatDateTime(iso: string | null | undefined, language?: Language): string {
  const date = formatDate(iso, language);
  return date ? `${date}, ${time(new Date(iso as string))}` : '';
}
